import { useMutation, useQuery } from '@tanstack/react-query'

import { StorageService } from '@/services/core/storage'
import type { Network } from '@/services/networks/networksApi'
import {
  ReleaseNotesApi,
  type ReleaseNote,
  type ReleaseNoteApp,
} from '@/services/releaseNotes/releaseNotesApi'
import { parseProfileValue } from '@/services/users/userProfileApi'
import { useCurrentUser } from '../contexts/UserContext'
import { useUserProfile } from '../contexts/UserProfileContext'
import { useNetwork } from '../contexts/NetworkContext'

/** Profile key holding when this user last read the release notes — this UI's own. */
export const RELEASE_NOTES_READ_KEY = 'ruiReleaseNotesLastRead'

/**
 * Someone who has never read any release notes is shown the recent ones only,
 * not every note ever written.
 */
const FIRST_VISIT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

/** The apps a note can be written for that the network runs. */
function networkApps(network: Network | null): ReleaseNoteApp[] {
  if (!network) return []
  const apps: ReleaseNoteApp[] = []
  if (network.advertisingPlatform) apps.push('ADSERVER')
  if (network.marketplacePlatform) apps.push('MARKETPLACE')
  if (network.dataPlatform) apps.push('DATA')
  return apps
}

/**
 * When the user last read the release notes, here or in the old UI, as epoch
 * milliseconds — null if never. The old UI keeps it in its `adnPrefs` blob in
 * seconds; it is only read here, never written, so the two cannot clobber
 * each other's preferences.
 */
function lastReadOf(profile: Record<string, string>): number | null {
  const ours = Date.parse(parseProfileValue(profile[RELEASE_NOTES_READ_KEY], ''))
  const uiiSeconds = Number(
    parseProfileValue<{ lastReleaseNotesRead?: unknown } | null>(profile.adnPrefs, null)?.lastReleaseNotesRead
  )
  const times = [ours, uiiSeconds * 1000].filter((t) => Number.isFinite(t) && t > 0)
  return times.length ? Math.max(...times) : null
}

/**
 * The release notes for the signed-in user: every one written for the apps
 * their network runs, which of them they have not read yet, and a way to mark
 * them read.
 */
export function useWhatsNew() {
  const user = useCurrentUser()
  const { currentNetwork } = useNetwork()
  const { profile, isLoading: lastReadLoading, save } = useUserProfile()
  // Support staff acting as someone else must not read their news for them.
  const isMasquerading = !!StorageService.getMasquerade()

  const { data: allNotes = [], isLoading: notesLoading } = useQuery({
    queryKey: ['release-notes', 'published', user?.id],
    queryFn: ReleaseNotesApi.getPublished,
    enabled: !!user,
    staleTime: 10 * 60_000,
  })

  const lastRead = profile ? lastReadOf(profile) : null

  const apps = networkApps(currentNetwork)
  const notes = allNotes.filter(
    (note) => note.apps.length === 0 || note.apps.some((app) => apps.includes(app))
  )

  const since = lastRead ?? Date.now() - FIRST_VISIT_WINDOW_MS
  const unread: ReleaseNote[] = lastReadLoading
    ? []
    : notes.filter((note) => Date.parse(note.publishTime) > since)

  const markRead = useMutation({
    mutationFn: () => save({ [RELEASE_NOTES_READ_KEY]: new Date().toISOString() }),
  })

  return {
    notes,
    unread,
    isLoading: notesLoading || lastReadLoading,
    isUnread: (note: ReleaseNote) => unread.includes(note),
    /** Marks everything published so far as read. A no-op while masquerading. */
    markRead: () => {
      if (!isMasquerading && unread.length > 0 && !markRead.isPending) markRead.mutate()
    },
    isMasquerading,
  }
}
